import {
  Chart,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  TimeScale,
  PointElement,
  LineElement,
} from "chart.js/auto";
import zoomPlugin from 'chartjs-plugin-zoom';
import { useEffect, useRef, useState } from "react";
import { Bar, Line, Doughnut, Pie } from "react-chartjs-2";
import Loader from "./Loader";
import { useTranslation } from "react-i18next";

export default function ChartData({
  horizontal,
  dataForChart,
  allLabels,
  isStacked,
  titleChart,
  handleGraphClick,
  reporting,
  typeOfChart,
  measurementUnit,
  measurementUnitBandwidth,
  maxValueLeft,
  minValueLeft,
  tension,
  radiusGraph,
  setNumberTicksX,
  setNumberTicksY,
  setNumberMaxTicksLimitY,
  setNumberRotate,
  maxHeight,
  addUnit,
  automaticColors,
  interactionStatus,
  dynamicLegend,
  dynamicDataLegend,
  customColors,
  theme,
  graphId,
  textColorOverride
}) {
  const chartRef = useRef();
  Chart.register(
    Title,
    Tooltip,
    Legend,
    CategoryScale,
    LinearScale,
    BarElement,
    TimeScale,
    PointElement,
    LineElement,
    zoomPlugin
  );

  const { t } = useTranslation();
  const [chartData, setChartData] = useState({});
  const [options, setOptions] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [autoColors, setAutoColors] = useState(true);
  const [chartKey, setChartKey] = useState(0);
  const [centerText, setCenterText] = useState("");
  const chartTextColor = textColorOverride || (theme === "dark" ? "#ffffff" : "#303030");

  useEffect(() => {
    setChartKey(prevKey => prevKey + 1);
  }, [titleChart, theme]);

  const defaultColors = {
    Incident: "rgba(255, 149, 5, 0.8)",
    "Changement mineur": typeOfChart === "line" ? theme !== 'dark' ? "rgba(18,65,136, 0.7)" : "rgba(153, 122, 219, 1)" : "rgba(18,65,136, 0.7)",
    "Changement majeur": "rgba(102, 102, 102, 1)",
    "Other Hardware": "#6ccae9",
    "Desktop Request": "#e96c6c",
    "Change request": "#5bc69a",
    "Quick Time": "#edd958",
    "Incident hors-contrat": "#FF0800",
    "Evénements": "rgba(153, 122, 219, 1)",
    "Nombre d'équipements": "rgba(153, 122, 219, 1)",
    Question: "#E8EBED",
    "Service Request": "#8977B0",
    "Alerte": "#FF5D57",
    Autre: "#9f5927",
    "Basse S4": "#E8EBED",
    "Critique S1": "rgba(255, 96, 84, 1)",
    "Haute S2": "rgba(255, 188, 44, 1)",
    "Moyenne S3": "rgba(255, 231, 93, 1)",
    "SLA RESPECTE": "rgba(174, 195, 83, 1)",
    "SLA DEPASSE": "rgba(153, 122, 219, 1)",
    YES: "rgba(144, 227, 154, 1)",
    NO: "rgba(255,0,0,0.5)",
    "Heures ouvrées Elit": "rgba(18,65,136, 0.7)",
    "Heures ouvrées totales": "rgba(174,195,83)",
    "Bandwidth": "#997adb",
    "Transmit": "#AEC353",
    "Receive": "#124188",
    "Total": "#997adb",
    Probleme: "rgba(246, 227, 19, 0.8)",
    "CpuUtilization": "#997adb",
    "MemoryUtilization": "#AEC353",
    "StorageUtilization": "#124188",
    "Unicast": "#997adb",
    "Multicast": "#AEC353",
    "Broadcast": "#124188",
    "Requêtes bloquées": "rgba(255, 149, 5, 0.8)",
    "2023": "rgb(255, 110, 100)",
    "2027": "rgb(255, 149, 90)",
    "2022": "rgb(255, 190, 30)",
    "2028": "rgb(255, 220, 80)",
    "2020": "rgb(140, 154, 255)",
    "2026": "rgb(143, 112, 209)",
    "Non annoncé": "rgb(50, 40, 20)",
    "1970": "rgb(112, 209, 127)",
    "2021": "rgb(90, 196, 245)",
    "2025": "rgb(243, 153, 153)",
    "2019": "rgb(243, 118, 118)",
    "2024": "rgb(255, 143, 92)",
    "2018": "rgb(173, 77, 77)",
    "WLC": "rgb(255, 110, 100)",
    "VG": "rgb(255, 149, 90)",
    "ISR": "rgb(255, 190, 30)",
    "Routeur": "rgb(255, 220, 80)",
    "SWITCH": "rgb(140, 154, 255)",
    "Produit non défini": "rgb(50, 40, 20)",
    "AP": "rgb(143, 112, 209)",
    "Serveur": "rgb(112, 209, 127)",
  };

  const colors = { ...defaultColors, ...customColors };

  const fixedColors = [
    "#997adb",
    "#AEC353",
    "#939599",
    "#124188",
    "#C9AE9D",
    "#BD4735",
    "#EDDC47"
  ];

  const generateRandomColor = () => {
    const r = Math.floor(Math.random() * 256);
    const g = Math.floor(Math.random() * 256);
    const b = Math.floor(Math.random() * 256);
    return `rgba(${r}, ${g}, ${b}, 0.8)`;
  };

  const getColorForLabel = (index) => {
    if (automaticColors && index >= fixedColors.length) {
      return generateRandomColor();
    }
    return fixedColors[index];
  };

  const textInSegment = {
    id: 'textInSegment',
    afterDatasetsDraw(chart) {
      const { ctx, data } = chart;
      ctx.save();
      data.datasets[0].data.forEach((value, index) => {
        const meta = chart.getDatasetMeta(0).data[index];
        const x = meta.tooltipPosition().x;
        const y = meta.tooltipPosition().y;
        ctx.fillStyle = "#ffffff";
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.font = 'bold 14px sans-serif';
        ctx.fillText(value, x, y);
      });
      ctx.restore();
    }
  };

  const verticalLinePlugin = {
    id: 'verticalLine',
    beforeDraw: (chart) => {
      const tooltip = chart.tooltip;
      if (tooltip && tooltip._active && tooltip._active.length) {
        const ctx = chart.ctx;
        const activePoint = tooltip._active[0];
        const x = activePoint.element.x;
        const yStart = activePoint.element.y;
        const yEnd = chart.chartArea.bottom;
        let strokeColor;
        if (titleChart === "Nombre de changements ouverts par mois" || titleChart === "Nombre de changements clos par mois") {
          strokeColor = theme === "dark" ? '#997adb' : '#124188';
        } else if (titleChart === "Nombre d'incidents ouverts par mois" || titleChart === "Nombre d'incidents clos par mois" || titleChart === "Nombre de requêtes bloquées par type d'identités") {
          strokeColor = "rgba(255, 188, 44, 1)";
        } else {
          strokeColor = theme === "dark" ? '#997adb' : '#124188';
        }
        ctx.save();
        ctx.beginPath();
        ctx.moveTo(x, yStart);
        ctx.lineTo(x, yEnd);
        ctx.lineWidth = 1;
        ctx.strokeStyle = strokeColor;
        ctx.setLineDash([4, 4]);
        ctx.stroke();
        ctx.restore();
      }
    }
  };

  const createChart = () => {
    const hasData = dataForChart && Object.keys(dataForChart).length > 0;
    let ticksLimit;
    if (setNumberMaxTicksLimitY) {
      const sumsByDate = Object.entries(dataForChart)
        .filter(([key, value]) => key !== "" && value !== null && Object.keys(value).length > 0)
        .reduce((acc, [date, values]) => {
          const sum = Object.values(values).reduce((total, value) => {
            if (value !== undefined) {
              return total + value;
            }
            return total;
          }, 0);
          acc[date] = sum;
          return acc;
        }, {});
      const Ticks = Object.values(sumsByDate).reduce((max, sum) => Math.max(max, sum), 0);
      const maxTicksToShow = 10;
      ticksLimit = Math.min(Ticks <= 1 ? Ticks + 1 : Ticks, maxTicksToShow);
      if (Ticks > maxTicksToShow) {
        ticksLimit = undefined;
      }
    }

    const maxTicksLimit = setNumberMaxTicksLimitY ? ticksLimit : undefined;

    if (typeOfChart === "doughnut") {
      const labels = allLabels;
      const colorsLabels = Object.values(colors);
      const backgroundColor = labels.map((_, index) => colorsLabels[index % colorsLabels.length]);

      const datasets = [
        {
          label: t('Nombre de tickets'),
          data: labels.map(label => dataForChart[label] || 0),
          backgroundColor: backgroundColor,
          borderColor: theme === "dark" ? "#303030" : "#ffffff",
          borderWidth: 1,
          hoverOffset: 20
        }
      ];

      const total = datasets[0].data.reduce((sum, val) => sum + val, 0);

      setCenterText({
        id: 'centerText',
        beforeDraw: (chart) => {
          const { ctx, chartArea: { left, right, top, bottom } } = chart;
          const centerX = (left + right) / 2;
          const centerY = (top + bottom) / 2;

          ctx.save();
          ctx.font = 'bold 16px Inter';
            ctx.fillStyle = chartTextColor;
          ctx.textAlign = 'center';
          ctx.textBaseline = 'middle';
          ctx.fillText(`Total de tickets : ${total}`, centerX, centerY);
          ctx.restore();
        }
      });

      setChartData({ labels, datasets });

      setOptions({
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          title: {
            display: true,
            position: "top",
            text: t(titleChart),
            align: "center",
            padding: 20,
            color: chartTextColor,
            font: {
              family: "Inter",
              size: "15",
            },
          },
          legend: {
            labels: {
              padding: 15,
              color: chartTextColor,
              font: {
                family: "Inter",
                size: 11,
                weight: 700
              },
              usePointStyle: true,
            }
          }
        },
      });
    } else {
      const labels = Object.keys(dataForChart)
        .filter((item) => item !== "")
        .sort(function (a, b) {
          return new Date(a) - new Date(b);
        });

      const datasets = allLabels.map((label, index) => {
        colors[label] !== undefined ? setAutoColors(false) : setAutoColors(true);

        return {
          label: t(label),
          data: labels.map((name) => dataForChart[name][label] || 0),
          borderRadius: 3,
          borderColor: automaticColors ? getColorForLabel(index) : colors[label],
          backgroundColor: (context) => {
            if (typeOfChart === 'line') {
              const chart = context.chart;
              const { ctx, chartArea } = chart;
              if (!chartArea) {
                return null;
              }

              [
                'Nombre de changements ouverts par mois',
                'Nombre de changements clos par mois',
                'Nombre d\'incidents ouverts par mois',
                'Nombre d\'incidents clos par mois',
              ]
              const gradient = ctx.createLinearGradient(0, chartArea.bottom, 0, chartArea.top);
              let startColor, endColor;

              if (titleChart === "Nombre de changements ouverts par mois" || titleChart === "Nombre de changements clos par mois") {
                startColor = theme === "dark" ? 'rgba(104, 104, 104, 0)' : 'rgba(255, 255, 255, 0)';
                endColor = theme === "dark" ? 'rgba(153, 122, 219, 1)' : 'rgba(18, 65, 136, 0.15)';
              } else if (titleChart === "Nombre d'incidents ouverts par mois" || titleChart === "Nombre d'incidents clos par mois" || titleChart === "Nombre de requêtes bloquées par type d'identités") {
                startColor = theme === "dark" ? 'rgba(104, 104, 104, 0)' : 'rgba(255, 255, 255, 0)';
                endColor = theme === "dark" ? 'rgba(255, 188, 44, 1)' : 'rgba(136, 102, 18, 0.15)';
              } else {
                startColor = theme === "dark" ? 'rgba(104, 104, 104, 0)' : 'rgba(255, 255, 255, 0)';
                endColor = theme === "dark" ? 'rgba(153, 122, 219, 1)' : 'rgba(18, 65, 136, 0.15)';
              }

              gradient.addColorStop(0, startColor);
              gradient.addColorStop(1, endColor);

              return gradient;
            }
            return automaticColors ? getColorForLabel(index) : colors[label];
          },
          fill: typeOfChart === 'line',
          tension: typeOfChart === 'line' ? 0.4 : 0,
          pointRadius: typeOfChart === 'line' ? 0 : 3,
          pointHoverRadius: typeOfChart === 'line' ? 7 : 3,
          pointHoverBackgroundColor: (() => {
            if (titleChart === "Nombre de changements ouverts par mois" || titleChart === "Nombre de changements clos par mois") {
              return theme === "dark" ? 'rgba(153, 122, 219, 0.87)' : 'rgba(18, 65, 136, 0.87)';
            } else if (titleChart === "Nombre d'incidents ouverts par mois" || titleChart === "Nombre d'incidents clos par mois" || titleChart === "Nombre de requêtes bloquées par type d'identités") {
              return 'rgba(255, 188, 44, 0.75)';
            } else {
              return theme === "dark" ? 'rgba(153, 122, 219, 0.87)' : 'rgba(18, 65, 136, 0.87)';
            }
          })(),
          pointHoverBorderColor: 'white',
          pointHoverBorderWidth: 2,
        };
      });

      setChartData({ labels, datasets });
      setOptions({
        responsive: true,
        indexAxis: horizontal ? "y" : "x",
        maintainAspectRatio: false,
        tension: tension ? tension : null,
        plugins: {
          zoom: (hasData && typeOfChart === "line") ? {
            pan: {
                enabled: true,
                mode: 'x',
            },
            zoom: {
                drag: {
                    enabled: true,
                    backgroundColor: 'rgba(216, 216, 216, 0.3)',
                    borderColor: 'rgba(176, 176, 176, 1)',
                    borderWidth: 1,
                },
                mode: 'x',
            },
          } : false,
          colors: {
            forceOverride: automaticColors ? false : autoColors,
          },
          title: {
            display: true,
            position: "top",
            text: t(titleChart),
            align: "center",
            padding: 20,
            color: chartTextColor,
            font: {
              family: "Inter",
              size: "15",
            },
          },
          legend: {
            labels: {
              padding: 10,
              color: chartTextColor,
              font: {
                family: "Inter",
                size: 11,
                weight: 700
              },
              usePointStyle: true,
            },
            onClick: (e, legendItem, legend) => {
              const chart = chartRef.current;
              if (dynamicLegend && chartRef.current) {
                const datasetIndex = legendItem.datasetIndex;
                const isHidden = chart.data.datasets[datasetIndex].hidden;
                chart.data.datasets[datasetIndex].hidden = !isHidden;
                const totalDataset = chart.data.datasets[0];
                const visibleData = Array(totalDataset.data.length).fill(0);
                chart.data.datasets.forEach((dataset, index) => {
                  if (!dataset.hidden && index !== 0) {
                    dataset.data.forEach((value, i) => {
                      visibleData[i] += value;
                    });
                  }
                });
                totalDataset.data = visibleData;
                chart.update();
              } else if (dynamicDataLegend && chartRef.current) {
                if (!chart.data.datasets[0].originalData) {
                  chart.data.datasets[0].originalData = [...chart.data.datasets[0].data];
                }
                const datasetIndex = legendItem.datasetIndex;
                const dataAgent = dynamicDataLegend.ticketsByAgent;
                const isHidden = chart.data.datasets[datasetIndex].hidden;
                chart.data.datasets[datasetIndex].hidden = !isHidden;
                dataAgent.data.forEach((monthData) => {
                  const monthIndex = chart.data.labels.indexOf(monthData.month);
                  if (monthIndex !== -1) {
                    const ticketsDetails = dynamicDataLegend.state ? monthData.opened_tickets_details : monthData.closed_tickets_details;
                    Object.keys(ticketsDetails).forEach((category) => {
                      const ticketsForCategory = parseInt(ticketsDetails[category]);
                      if (chart.data.datasets[datasetIndex].label === category) {
                        if (isHidden) {
                          chart.data.datasets[0].data[monthIndex] += ticketsForCategory;
                        } else {
                          chart.data.datasets[0].data[monthIndex] -= ticketsForCategory;
                        }
                      }
                    });
                    chart.data.datasets[0].data[monthIndex] = Math.max(0, chart.data.datasets[0].data[monthIndex]);
                  }
                });
                chart.update();
              } else {
                if (chart) {
                  Chart.defaults.plugins.legend.onClick.call(this, e, legendItem, legend);
                }
              }
            }
          },
          tooltip: {
            callbacks: {
              label: function (context) {
                let label = context.dataset.label || '';
                const tooltipItems = context.chart.tooltip.dataPoints;
                const value = context.raw;

                if (typeof graphId !== 'undefined' && graphId === 'ielo') {
                    const title = titleChart ? titleChart.toLowerCase() : '';
                    const absValue = Math.abs(value);
                    let formattedValue = value;

                    if (title.includes('traffic')) {
                        if (absValue >= 1000000000) formattedValue = (value / 1000000000).toFixed(2) + ' Gb/s';
                        else if (absValue >= 1000000) formattedValue = (value / 1000000).toFixed(2) + ' Mb/s';
                        else if (absValue >= 1000) formattedValue = (value / 1000).toFixed(2) + ' Kb/s';
                        else formattedValue = value + ' Bits/s';
                    } 
                    else if (title.includes('pkt')) {
                        if (absValue >= 1000000) formattedValue = (value / 1000000).toFixed(2) + ' MPkt/s';
                        else if (absValue >= 1000) formattedValue = (value / 1000).toFixed(2) + ' KPkt/s';
                        else formattedValue = value + ' Pkt/s';
                    } 
                    else {
                        formattedValue = value + ' /s';
                    }
                    return `${label} : ${formattedValue}`;
                }
                
                let formattedValue = '';
                if (label === 'Total') {
                  const total = tooltipItems
                    .filter(item => item.dataset.label !== 'Total')
                    .reduce((sum, item) => sum + item.raw, 0);
                  if (total >= 60) {
                    const hours = Math.floor(total / 60);
                    const minutes = total % 60;
                    formattedValue = minutes > 0 ? `${hours}h${minutes}min` : `${hours}h`;
                  } else {
                    formattedValue = `${total}min`;
                  }
                  return addUnit ? `${label}: ${formattedValue}` : `${label}: ${value}`;
                } else {
                  if (value >= 60) {
                    const hours = Math.floor(value / 60);
                    const minutes = Math.floor(value % 60);
                    formattedValue = minutes > 0 ? `${hours}h${minutes}min` : `${hours}h`;
                  } else {
                    formattedValue = `${value}min`;
                  }
                  return addUnit ? `${label}: ${formattedValue}` : `${label}: ${value}`;
                }
              }
            }
          }
        },
        scales: {
          x: {
            stacked: !!isStacked,
            ticks: {
              color: chartTextColor,
              font: {
                size: 10,
                family: "Inter",
                weight: 800
              },
              maxRotation: setNumberRotate ? setNumberRotate : undefined,
              maxTicksLimit: setNumberTicksX ? setNumberTicksX : undefined,
              
              callback: function (value, index, values) {
                const label = this.getLabelForValue(value);
                if (typeof graphId !== 'undefined' && graphId === 'ielo') {
                
                   let date;
                    const parts = label.split(' ');
                    if (parts.length === 2) {
                        const dateParts = parts[0].split('/'); 
                        const timeParts = parts[1].split(':'); 
                        
                        if (dateParts.length === 3 && timeParts.length >= 2) {
                            date = new Date(
                                parseInt(dateParts[2], 10),
                                parseInt(dateParts[1], 10) - 1,
                                parseInt(dateParts[0], 10),
                                parseInt(timeParts[0], 10),
                                parseInt(timeParts[1], 10)
                            );
                        }
                    }

                    if (!date || isNaN(date.getTime())) {
                        return null; 
                    }

                    if (date.getHours() % 6 === 0 && date.getMinutes() === 0) {
                        const day = String(date.getDate()).padStart(2, '0');
                        const month = String(date.getMonth() + 1).padStart(2, '0');
                        const hours = String(date.getHours()).padStart(2, '0');
                        const minutes = String(date.getMinutes()).padStart(2, '0');
                        
                        return `${day}/${month} ${hours}:${minutes}`;
                    }
                  
                    return null;
                }
                return this.getLabelForValue(value);
              }
            },
            grid: {
              display: false,
            },
          },
          y: {
            beginAtZero: true,
            stacked: !!isStacked,
            max: maxValueLeft ? maxValueLeft : undefined,
            min: minValueLeft === Number() ? minValueLeft : undefined,
            ticks: {
              color: chartTextColor,
              font: {
                size: 10,
                family: "Inter",
                weight: 800
              },
              maxTicksLimit: setNumberTicksY ? setNumberTicksY : maxTicksLimit,
              callback: function (value) {
                if (typeof graphId !== 'undefined' && graphId === 'ielo') {
                    if (value === 0) return '0';
                    const absValue = Math.abs(value);
                    
                    const title = titleChart ? titleChart.toLowerCase() : '';

                    if (title.includes('traffic')) {
                        if (absValue >= 1000000000) return (value / 1000000000).toFixed(1) + ' Gb/s';
                        if (absValue >= 1000000) return (value / 1000000).toFixed(1) + ' Mb/s';
                        if (absValue >= 1000) return (value / 1000).toFixed(1) + ' Kb/s';
                        return value + ' Bits/s';
                    }
                    
                    if (title.includes('pkt')) {
                        if (absValue >= 1000000000) return (value / 1000000000).toFixed(1) + ' GPkt/s';
                        if (absValue >= 1000000) return (value / 1000000).toFixed(1) + ' MPkt/s';
                        if (absValue >= 1000) return (value / 1000).toFixed(1) + ' KPkt/s';
                        return value + ' Pkt/s';
                    }

                    if (absValue >= 1000000) return (value / 1000000).toFixed(1) + ' M/s';
                    if (absValue >= 1000) return (value / 1000).toFixed(1) + ' k/s';
                    return value + ' /s';
                }
                if (measurementUnitBandwidth) {
                  const [useUnits, unitGbit, unitMbit, unitKbit] = measurementUnitBandwidth;
                  const valueDecimal = this.getLabelForValue(value).replace(/,/g, '');
                  const labelValue = parseFloat(this.getLabelForValue(value).replace(/\s/g, ''));
                  if (useUnits && unitGbit || unitMbit || unitKbit) {
                    return valueDecimal >= 1000000 ? labelValue + ' ' + unitGbit : (valueDecimal >= 1000 ? labelValue + ' ' + unitMbit : labelValue + ' ' + unitKbit);
                  } else {
                    return measurementUnit ? valueDecimal + ' ' + measurementUnit : this.getLabelForValue(value);
                  }
                } else {
                  const intValue = this.getLabelForValue(Math.trunc(value));
                  return measurementUnit ?
                    (value % 1 === 0 ? intValue + ' ' + measurementUnit : '') :
                    (value % 1 === 0 ? intValue : '');
                }
              },
            },
            grid: {
              color: theme === "dark" ? "#ffffff" : "#303030",
              lineWidth: 0.2,
            },
          },
        },
        elements: {
          point: {
            radius: radiusGraph ? radiusGraph : undefined,
          },
        },
        interaction: typeOfChart !== "line" ? interactionStatus : {
          mode: 'index',
          intersect: false
        },
      });
    }
  };

  useEffect(() => {
    createChart();
    setIsLoading(false);
  }, [dataForChart, allLabels, autoColors]);

  return (
    <>
      {reporting ? (
        !isLoading ? (
          <div className="page-chart">
            <Bar
              key={chartKey}
              data={chartData}
              width={30}
              height={400}
              options={options}
              ref={chartRef}
              onClick={(event) =>
                handleGraphClick
                  ? handleGraphClick(event, chartRef.current)
                  : undefined
              }
            />
          </div>
        ) : (
          <Loader />
        )
      ) : (
        <> {!isLoading ? <>
          <section className="page-chart">
            {typeOfChart === "bar" ? (
              <Bar
                key={chartKey}
                data={chartData}
                width={100}
                height={maxHeight ? maxHeight : 600}
                options={options}
                ref={chartRef}
                onClick={(event) =>
                  handleGraphClick
                    ? handleGraphClick(event, chartRef.current)
                    : undefined
                }
              />
            ) : typeOfChart === "doughnut" ? (
              <Doughnut data={chartData}
                key={chartKey}
                width={100}
                height={maxHeight ? maxHeight : 600}
                options={options}
                ref={chartRef}
                plugins={[textInSegment]}
                onClick={(event) =>
                  handleGraphClick
                    ? handleGraphClick(event, chartRef.current)
                    : undefined
                }
              />
            ) : (
              <Line
                key={chartKey}
                data={chartData}
                ref={chartRef}
                width={100}
                height={maxHeight ? maxHeight : 600}
                options={options}
                plugins={[verticalLinePlugin]}
                onDoubleClick={() => { if (chartRef.current) chartRef.current.resetZoom(); }}
              />
            )}
          </section>
        </> : <Loader />}</>
      )}
    </>
  );
}
